import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import { RootState } from 'src/redux-store/appStore';
import { httpRequest } from 'src/services/httpRequest';
import EditApplicationForUser from './user';
import EditApplicationForManager from './manager';
import EditFormfeatureViews from 'src/components/EditFormfeatureViews/EditFormfeatureViews';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { handleError } from 'src/redux-store/slices/errorSlice';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { getFeesForCurrentPermit } from '../../common/getFeesForCurrentPermit';
import { sendNotification } from 'src/utils/sendNotification';
import { getApplicationDetails } from '../../common/getApplicationDetails';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';

interface ApplicationDataTypes {
  ApplicationNumber: string;
  ApplicationStatus: string;
  ApplicationID: string;
  TownshipID: string;
  UserID: number;
  InspectionDate: string;
  OwnerAddress: string;
  RemovalPurpose: string;
  Condition: string;
  NoOfTrees: string;
  AdditionalEscrowFee: number;
}

function EditApplicationTreePermit() {
  const socket = useSelector((store: RootState) => store.socket.socket);
  const dispatch = useDispatch();
  const currentPermitDetails = useSelector(
    (state: RootState) => state?.currentPermit?.currentPermitDetails
  );
  const {permit, applicationID} = useParams()
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}`;
  const isManager = currentPermitDetails.currentPermitUserRole === 'Manager';
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const [switchTo, setSwitchTo] = useState<string>('');
  const [taxRate, setTaxRate] = useState<number>(0);
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [blockLotsOptions, setBlockLotsOptions] = useState<[]>();
  const [clickedOn, setClickedOn] = useState<string>('');
  const [applicationData, setApplicationData] =
    useState<ApplicationDataTypes>();
  const [replacementMethodData, setReplacementMethodData] = useState({
    vendorToReplace: '',
    townshipToReplace: ''
  });
  const [treePermitFormData, setTreePermitFormData] = useState({
    blockLot: '',
    owner: '',
    treeAddress: '',
    nearBy: '',
    phone: '',
    numberOfTrees: '',
    purpose: '',
    lotSize: '',
    areaConfirmationCheck: ''
  } as any);
  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${currentPermitDetails?.id}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: Number(feeObject?.ApplicationFee),
        inspectionFee: Number(feeObject?.InspectionFee)
      });
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
      setLoading(false);
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };
  const handleGetApplicationDetails = async()=>{
    setLoading(true);
    const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft
    const response = await getApplicationDetails({applicationID, permit, isDraft})
    if(response?.status === "success"){
      setLoading(false);
      const data = response?.data?.data[0]
      const formData = {
        blockLot: data?.BlockLots,
        owner: data?.OwnerName,
        treeAddress: data?.OwnerAddress,
        nearBy: data?.NearestCrossStreet,
        phone: data?.OwnerPhoneNumber,
        numberOfTrees: data?.NoOfTrees,
        purpose: data?.RemovalPurpose,
        lotSize: data?.LotSize,
        areaConfirmationCheck: data?.TreesInRestrictedArea,
        sitePlanApproval: data?.SitePlanApproval,
        inspectionDate: data?.InspectionDate,
        conditions: data?.Condition
      };
      setTreePermitFormData(formData)
        setApplicationData(response?.data?.data[0])
        if (currentPermitDetails?.id && isDraft) handleGetApplicationFee();
    }
}

useEffect(() => {
  handleGetApplicationDetails()
}, [applicationID, permit]);

  const handleBlockLotsList = async (val) => {
    if (val) {
      if (val && val?.blockLot) {
        setTreePermitFormData({
          ...treePermitFormData,
          blockLot: val?.BlockLot,
          owner: val?.Owner,
          treeAddress: val?.PropAddress
        });
      }
      const response = await httpRequest(
        'GET',
        `/propertyDetails/searchBlockLots_Address?BlockLotPrefix=${val}`
      );
      if (response?.status === 'success') {
        const list = response?.data
          ?.map((value: { BlockLot: string }) => {
            return { ...value, label: value?.BlockLot };
          })
          ?.slice(0, 10);
        setBlockLotsOptions(list);
      }
    }
  };

  const handleUpdate = async (data, Params = '') => {
    const requestData = { ...treePermitFormData, ...data };
    setTreePermitFormData(requestData);

    setLoading(true);
    const payload = {
      BlockLots: requestData?.blockLot,
      OwnerAddress: requestData?.treeAddress,
      OwnerName: requestData?.owner,
      OwnerPhoneNumber: requestData?.phone,
      NoOfTrees: requestData?.numberOfTrees,
      RemovalPurpose: requestData?.purpose,
      TreesInRestrictedArea: requestData?.areaConfirmationCheck,
      SitePlanApproval: requestData?.sitePlanApproval,
      LotSize: requestData?.lotSize,
      NearestCrossStreet: requestData?.nearBy,
      Condition: data?.condition || requestData?.conditions, 
      InspectionDate: format(
        new Date(data?.inspectionDate || requestData?.inspectionDate),
        'MM/dd/yyyy'
      ) 
    };

    const response = await httpRequest(
      'PUT',
      `/tree_permit_application/update/${applicationData?.ApplicationID}`,
      payload
    );
    if (!Params) {
      if (response?.status === 'success') {
        setLoading(false);
        toast?.success('Application updated successfully.');
        navigate(
          `${basePath}/${
            applicationData?.ApplicationStatus === 'Draft'
              ? 'draft-application'
              : 'application-list'
          }`
        );
      } else {
        toast?.error(response?.message);
        setLoading(false);
        dispatch(handleError(response?.message));
      }
    }
    return response?.status === 'success';
  };

  const handleProceedToPay = async (params) => {
    const isUpdated = await handleUpdate(params, 'PaymentProcess');
    if (isUpdated) {
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: applicationData?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'Await CO',
          permitPrefix: 'Tree'
        }
      };
      navigate(
        `/payment-methods`,
        { state: { paymentSummary, paymentApplicationDetails } }
      );
    }
  };

  const handleSubmitOnUpdateAndPayment = async (data) => {
    if (clickedOn === 'Proceed to Payment') {
      await handleProceedToPay(data);
    } else if (clickedOn === 'Update' || clickedOn === 'Update Condition / Inspection Date') {
      await handleUpdate(data);
    }
  };

  const handleApproveDeny = async (param) => {
    setLoading(true);
    const payload = {
      ApplicationID: Number(applicationData?.ApplicationID),
      ApplicationStatus: param
    };

    const approvedPayload = {
      Address: applicationData?.OwnerAddress || '',
      Purpose: applicationData?.RemovalPurpose || '',
      Conditions: applicationData?.Condition || '',
      NumberOfTrees: applicationData?.NoOfTrees || '',
      AdditionalFee: applicationData?.AdditionalEscrowFee || 0
    };

    const response = await httpRequest(
      'PUT',
      `/applications/${currentPermitDetails?.id}/update_status`,
      param === 'Approved' ? { ...payload, ...approvedPayload } : payload
    );
    if (response?.status === 'success') {
      sendNotification(socket, {
        UserID: applicationData?.UserID, 
        TownshipID: applicationData?.TownshipID,
        ApplicationID: applicationData?.ApplicationID,
        PermitID: currentPermitDetails?.id,
        Title: 'Application Approved',
        Message: `Tree Removal/Replacement application with the no. (${applicationData?.ApplicationNumber}) is approved successfully.`,
        ApplicationNumber: applicationData?.ApplicationNumber
      });
      toast?.success(`Application ${param.toLowerCase()} successfully.`);
      navigate(`${basePath}/application-list`);
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setLoading(false);
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Grid item md={3} sm={3} sx={{ p: 3, pt: 2 }}>
      <Breadcrumb
        PermitName={'Tree Removal'}
        PageTitle={'Edit Application'}
      />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType="edit"
        applicationNumber={applicationData?.ApplicationNumber}
        applicationStatus={applicationData?.ApplicationStatus}
      />
      {switchTo !== '' ? (
        <EditFormfeatureViews
          switchTo={switchTo}
          setSwitchTo={setSwitchTo}
          applicationDetails={{
            applicationID: applicationData?.ApplicationID,
            TownshipID: applicationData?.TownshipID,
            UserID: applicationData?.UserID
          }}
        />
      ) : (
        <>
          {!isManager ? (
            <EditApplicationForUser
              handleBlockLotsList={handleBlockLotsList}
              blockLotsOptions={blockLotsOptions}
              setSwitchTo={setSwitchTo}
              treePermitFormData={treePermitFormData}
              setTreePermitFormData={setTreePermitFormData}
              applicationData={applicationData}
              handleUpdate={handleUpdate}
              setClickedOn={setClickedOn}
              handleSubmitOnUpdateAndPayment={handleSubmitOnUpdateAndPayment}
            />
          ) : (
            <EditApplicationForManager
              setClickedOn={setClickedOn}
              handleBlockLotsList={handleBlockLotsList}
              blockLotsOptions={blockLotsOptions}
              setSwitchTo={setSwitchTo}
              treePermitFormData={treePermitFormData}
              setTreePermitFormData={setTreePermitFormData}
              applicationData={applicationData}
              replacementMethodData={replacementMethodData}
              setReplacementMethodData={setReplacementMethodData}
              handleApproveDeny={handleApproveDeny}
              handleSubmitOnUpdateAndPayment={handleSubmitOnUpdateAndPayment}
            />
          )}
        </>
      )}
    </Grid>
  );
}

export default EditApplicationTreePermit;
